import {CopyToClipboardIconButton} from '@github-ui/copy-to-clipboard/IconButton'
import {createReactRootErrorHandlers} from '@github-ui/react-core/react-root-error-handlers'
import {isUIService} from '@github-ui/runtime-environment'
import {useEffect} from 'react'
import {createRoot} from 'react-dom/client'

const hiddenTimePeriodOnScrollInMs = 1000

/**
 * Mounts a React `CopyToClipboardIconButton` into a single snippet element, mirroring
 * `insertSnippetClipboardCopyButton` from the global
 * `app/assets/modules/github/behaviors/snippet-clipboard-copy.ts` behavior.
 *
 * Dotcom renders the `#snippet-clipboard-copy-button` templates from
 * `app/views/site/_snippet_clipboard_copy_template.html.erb` and hydrates them with the
 * `clipboard-copy` element, but the UI Service never renders that partial. Rather than
 * recreate the legacy `clipboard-copy` markup, we mount the shared React copy button into
 * a container appended to the snippet.
 *
 * Returns a teardown that unmounts the React root and removes the container.
 */
function mountSnippetClipboardCopyButton(el: HTMLElement): (() => void) | undefined {
  const clipboardContent = el.getAttribute('data-snippet-clipboard-copy-content')
  if (clipboardContent === null) return

  // Remove this attribute so that we don't add a second button.
  el.removeAttribute('data-snippet-clipboard-copy-content')

  const useUnpositioned = !!el.closest('.js-snippet-clipboard-copy-unpositioned')

  const container = document.createElement('div')
  if (!useUnpositioned) {
    container.className = 'position-absolute right-0 top-0'
  }
  el.appendChild(container)

  const root = createRoot(container, createReactRootErrorHandlers({appName: 'snippet-clipboard-copy'}))
  root.render(
    <CopyToClipboardIconButton
      textToCopy={clipboardContent}
      ariaLabel="Copy code to clipboard"
      className="m-2"
      tooltipProps={{direction: 'w'}}
    />,
  )

  const controller = new AbortController()
  const {signal} = controller

  if (!useUnpositioned) {
    document.addEventListener(
      'selectionchange',
      () => {
        const selection = document.getSelection()
        if (selection && el.contains(selection.anchorNode)) {
          const selectedText = selection.toString()
          container.style.display = selectedText.trim() === '' ? 'inherit' : 'none'
        }
      },
      {signal},
    )

    const scrollableElement = el.querySelector('pre')
    if (scrollableElement !== null) {
      let timer: ReturnType<typeof setTimeout> | undefined

      /* eslint-disable-next-line github/prefer-observers */
      scrollableElement.addEventListener(
        'scroll',
        () => {
          if (timer) clearTimeout(timer)

          container.style.display = 'none'
          timer = setTimeout(() => {
            container.style.display = 'inherit'
          }, hiddenTimePeriodOnScrollInMs)
        },
        {signal},
      )
    }
  }

  return () => {
    controller.abort()
    // Defer unmount so we never unmount a root synchronously while React is rendering the
    // parent tree (which triggers a warning). The container is removed after unmount.
    setTimeout(() => {
      root.unmount()
      container.remove()
    })
  }
}

/**
 * Adds copy-to-clipboard buttons to markdown code snippets within the UI Service.
 *
 * In dotcom this is handled by the global `snippet-clipboard-copy.ts` behavior via
 * selector-observer. In ui-service that behavior is not loaded, so this hook provides
 * the equivalent functionality scoped to the markdown container: mounting a
 * `CopyToClipboardIconButton` for each `[data-snippet-clipboard-copy-content]` snippet.
 *
 * This hook is a no-op outside the UI Service to avoid conflicting with the global behavior.
 */
export function useSnippetClipboardCopy(
  containerRef: React.RefObject<HTMLDivElement | null>,
  contentVersion?: unknown,
) {
  useEffect(() => {
    if (!isUIService) return

    const container = containerRef.current
    if (!container) return

    // Track the teardown for every mounted button so their React roots and listeners can
    // be cleaned up together when the container unmounts or the content changes.
    const teardowns = new Set<() => void>()

    const enrichContainer = () => {
      for (const el of container.querySelectorAll<HTMLElement>('[data-snippet-clipboard-copy-content]')) {
        const teardown = mountSnippetClipboardCopyButton(el)
        if (teardown) teardowns.add(teardown)
      }
    }

    enrichContainer()

    // Soft-nav may update rich text asynchronously. Observe the container so newly
    // inserted snippets still receive a clipboard copy button. Already-enriched snippets
    // have their `data-snippet-clipboard-copy-content` attribute removed, so re-running
    // enrichment after our own DOM insertions is a no-op (no infinite loop).
    const observer = new MutationObserver(() => {
      enrichContainer()
    })
    observer.observe(container, {childList: true, subtree: true})

    return () => {
      observer.disconnect()
      for (const teardown of teardowns) {
        teardown()
      }
      teardowns.clear()
    }
  }, [containerRef, contentVersion])
}
